import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useFeatureFlags, FEATURES } from '@/contexts/FeatureFlagContext';
import { useAuth, ROLES } from '@/contexts/AuthContext';
import {
  House,
  Users,
  CalendarDots,
  Bed,
  FirstAid,
  Flask,
  Receipt,
  Package,
  UsersThree,
  FolderOpen,
  ClockCounterClockwise,
  Gear,
  CaretLeft,
  CaretRight,
  UserGear,
  Calendar,
  ShieldCheck,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

const NAVIGATION_ITEMS = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    icon: House,
    feature: FEATURES.DASHBOARD,
  },
  {
    name: 'Patients',
    path: '/patients',
    icon: Users,
    feature: FEATURES.PATIENTS,
  },
  {
    name: 'Appointments',
    path: '/appointments',
    icon: CalendarDots,
    feature: FEATURES.APPOINTMENTS,
  },
  {
    name: 'IPD / Ward',
    path: '/ipd',
    icon: Bed,
    feature: FEATURES.IPD,
  },
  {
    name: 'Pharmacy',
    path: '/pharmacy',
    icon: FirstAid,
    feature: FEATURES.PHARMACY,
  },
  {
    name: 'Laboratory',
    path: '/lab',
    icon: Flask,
    feature: FEATURES.LAB,
  },
  {
    name: 'Billing',
    path: '/billing',
    icon: Receipt,
    feature: FEATURES.BILLING,
  },
  {
    name: 'Inventory',
    path: '/inventory',
    icon: Package,
    feature: FEATURES.INVENTORY,
  },
  {
    name: 'HR & Staff',
    path: '/hr',
    icon: UsersThree,
    feature: FEATURES.HR,
  },
  {
    name: 'Documents',
    path: '/documents',
    icon: FolderOpen,
    feature: FEATURES.DOCUMENTS,
  },
  {
    name: 'Audit Log',
    path: '/audit',
    icon: ClockCounterClockwise,
    feature: FEATURES.AUDIT,
  },
];

export const Sidebar = ({ isCollapsed, setIsCollapsed }) => {
  const location = useLocation();
  const { isFeatureEnabled } = useFeatureFlags();
  const { user, hasRole, hasModule } = useAuth();

  let visibleItems = NAVIGATION_ITEMS.filter(
    (item) =>
      isFeatureEnabled(item.feature) && hasModule(item.module ?? item.feature),
  );

  // Add role-specific navigation items
  const roleSpecificItems = [];
  
  if (
    hasRole([ROLES.SUPER_ADMIN, ROLES.HOSPITAL_ADMIN]) &&
    hasModule(FEATURES.USERS)
  ) {
    roleSpecificItems.push({
      name: 'User Management',
      path: '/users',
      icon: UserGear,
      feature: FEATURES.USERS,
      module: FEATURES.USERS,
    });
  }

  if (
    hasRole([ROLES.SUPER_ADMIN, ROLES.HOSPITAL_ADMIN]) &&
    hasModule(FEATURES.ROLE_MANAGEMENT)
  ) {
    roleSpecificItems.push({
      name: 'Role Management',
      path: '/roles',
      icon: ShieldCheck,
      feature: FEATURES.ROLE_MANAGEMENT,
      module: FEATURES.ROLE_MANAGEMENT,
    });
  }
  
  if (hasRole(ROLES.DOCTOR) && hasModule(FEATURES.MY_CALENDAR)) {
    roleSpecificItems.push({
      name: 'My Calendar',
      path: '/my-calendar',
      icon: Calendar,
      feature: FEATURES.MY_CALENDAR,
      module: FEATURES.MY_CALENDAR,
    });
  }

  if (roleSpecificItems.length > 0) {
    const dashboardIndex = visibleItems.findIndex((item) => item.path === '/dashboard');
    const insertAt = dashboardIndex >= 0 ? dashboardIndex + 1 : 0;
    visibleItems = [
      ...visibleItems.slice(0, insertAt),
      ...roleSpecificItems,
      ...visibleItems.slice(insertAt),
    ];
  }

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-screen bg-card border-r border-border transition-all duration-300 z-20',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex flex-col h-full">
        <div className={cn('flex items-center h-16 px-4 border-b border-border', isCollapsed && 'justify-center')}>
          {!isCollapsed && (
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
              <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">HMS</span>
              </div>
              <span className="font-bold text-lg tracking-tight">MediCare</span>
            </Link>
          )}
          {isCollapsed && (
            <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">HMS</span>
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          <div className="space-y-1 px-2">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`sidebar-nav-${item.path}`}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Icon className="h-5 w-5 shrink-0" weight={isActive ? 'fill' : 'regular'} />
                  {!isCollapsed && <span>{item.name}</span>}
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="border-t border-border p-2">
          <Separator className="mb-2" />
          <Link
            to="/settings"
            data-testid="sidebar-nav-settings"
            className={cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
              location.pathname === '/settings'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <Gear className="h-5 w-5 shrink-0" weight={location.pathname === '/settings' ? 'fill' : 'regular'} />
            {!isCollapsed && <span>Settings</span>}
          </Link>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-full mt-2 justify-center"
            data-testid="sidebar-collapse-toggle"
          >
            {isCollapsed ? (
              <CaretRight className="h-4 w-4" />
            ) : (
              <>
                <CaretLeft className="h-4 w-4 mr-2" />
                <span className="text-xs">Collapse</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </aside>
  );
};